import { gql, useQuery } from '@apollo/client';
import NpvByCountryThumbnail from '@assets/images/report-components/npv-by-country-thumbnail.png';
import { useEffect, useMemo, useState } from 'react';
import { GraphWrapper } from '@/admins/SmartReports/components/Components/Charts/GraphWrapper';
import { getLayout } from '@/admins/SmartReports/components/Components/Charts/helpers';
import { ComponentBox } from '@/admins/SmartReports/components/Components/Common/ComponentBox';
import { ComponentTitle } from '@/admins/SmartReports/components/Components/Common/ComponentTitle';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';
import {
  AssetAggregateByEnum,
  type AssetAggregates,
  AssetAggregatesFieldsEnum,
} from '@/types/GraphqlSchema';
import theme from '@/utils/theme';
import {
  type ComponentDefinition,
  type ComponentEditProps,
  ComponentViewMode,
  type ComponentViewProps,
  type UseDataType,
} from '../types';

const QUERY = gql`
  query AssetAggregates($input: AssetAggregatesInput!) {
    assetAggregates(input: $input) {
      type
      group
      npv0Sum
      npv5Sum
      npv10Sum
      npv15Sum
      npv20Sum
    }
  }
`;

export const COMPONENT_ID = 'npv_by_country_chart';

// NPV Series Configuration
const NPV_SERIES_CONFIG = [
  {
    key: AssetAggregatesFieldsEnum.Npv0Sum,
    name: 'Nominal',
    color: theme.palette.baseColors.deepSeaNavy.c100,
  },
  {
    key: AssetAggregatesFieldsEnum.Npv5Sum,
    name: 'NPV5',
    color: theme.palette.baseColors.fireOrange.c100,
  },
  {
    key: AssetAggregatesFieldsEnum.Npv10Sum,
    name: 'NPV10',
    color: theme.palette.baseColors.earthGreen.c100,
  },
  {
    key: AssetAggregatesFieldsEnum.Npv15Sum,
    name: 'NPV15',
    color: theme.palette.baseColors.amber.c100,
  },
  {
    key: AssetAggregatesFieldsEnum.Npv20Sum,
    name: 'NPV20',
    color: theme.palette.baseColors.skyBlue.c100,
  },
];

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<AssetAggregates[]>) => {
  const [chartDataFetched, setChartDataFetched] = useState(persistedData);

  const { data, loading, error } = useQuery<{ assetAggregates: AssetAggregates[] }>(QUERY, {
    variables: {
      input: {
        by: AssetAggregateByEnum.Country,
        fields: NPV_SERIES_CONFIG.map(series => series.key),
        portfolio,
        limit: 10,
        orderBy: AssetAggregatesFieldsEnum.Npv10Sum,
        order: 'DESC',
      },
    },
    skip: !!persistedData,
  });

  useEffect(() => {
    if (data?.assetAggregates) {
      const chartData = data.assetAggregates;
      setChartDataFetched(chartData);
      if (onComponentLoad) {
        onComponentLoad({
          componentId: COMPONENT_ID,
          data: chartData,
        });
      }
    }
  }, [data, onComponentLoad]);

  return { chartData: chartDataFetched, error, loading };
};

const ComponentView = ({
  data,
  mode = ComponentViewMode.View,
}: ComponentViewProps<AssetAggregates[]>) => {
  const formattedData = useMemo(() => {
    try {
      if (!data || !data.length) return null;

      const countries = data.map(agg => agg.group);

      const formattedChartData = NPV_SERIES_CONFIG.map(series => ({
        x: countries,
        y: data.map(agg => (agg[series.key as keyof AssetAggregates] as number) || 0),
        type: 'bar',
        name: series.name,
        marker: { color: series.color },
        hovertemplate: `%{x}<br>${series.name}: $%{y:,.2f} million<extra></extra>`,
      }));

      return formattedChartData;
    } catch {
      return null;
    }
  }, [data]);

  if (!formattedData) return null;

  return (
    <ComponentBox>
      <ComponentTitle>NPV by Top 10 Countries</ComponentTitle>

      <GraphWrapper
        data={formattedData}
        layout={getLayout({ yaxis: { title: { text: 'US$ million' } }, barmode: 'group' })}
        config={{
          staticPlot: mode !== ComponentViewMode.View,
        }}
      />
    </ComponentBox>
  );
};

const ComponentEdit = ({
  portfolio,
  persistedData,
  onComponentLoad,
}: ComponentEditProps<AssetAggregates[]>) => {
  const { chartData, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={chartData}>
      {chartData && <ComponentView data={chartData} mode={ComponentViewMode.Edit} />}
    </LoadingErrorWrapper>
  );
};

const ComponentThumbnail = () => (
  <ComponentBox>
    <ComponentTitle sx={{ fontSize: '0.25rem', marginBottom: 0 }}>
      NPV by Top10 Countries
    </ComponentTitle>
    <img
      src={NpvByCountryThumbnail}
      alt='NPV By Country Chart Thumbnail'
      style={{ width: '100%', height: 'auto' }}
    />
  </ComponentBox>
);

export const NpvByCountryComponent: ComponentDefinition<AssetAggregates[]> = {
  id: COMPONENT_ID,
  name: 'NPV by Top10 Countries',
  type: SmartReportComponentTypesEnum.Chart,
  reportTypes: [SmartReportTypeEnum.Corporate],
  Thumbnail: ComponentThumbnail,
  Component: ComponentEdit,
  ComponentView,
};
